import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import { Layout, Modal, Select, Button } from 'antd';
import { ExpandOutlined, PlayCircleOutlined, PauseCircleOutlined } from '@ant-design/icons';
import Ambientali from "../environmental-sensors/Ambientali";
import Imu from "../environmental-sensors/Imu";
import AudioChart from "../environmental-sensors/Audio";
import ConfrontoIMUGraph from "../environmental-sensors/ConfrontoIMUGraph";
import Proprieta from "./RoadVelocita.js";
import PointCloudDeckGL from "./NuvolaPunti";
import Flow from "./Flow";
import TravelTime from "./TravelTime";
import Info from "./Info";
import Interpreter from "./Interpreter";
import '../../styles/Sider.css';
import '../../styles/Proprieta.css';
import { BiVideoRecording } from "react-icons/bi";
import VelocitaIcon from "../../assets/icons/Velocita_icon.svg";
import FlowIcon from '../../assets/Congestion.svg';
import TravelTimeIcon from '../../assets/Anomaly.svg';
import dayjs from 'dayjs';
const { Sider } = Layout;

function SiderComponent({
    isMobile,
    collapsed,
    setCollapsed,
    siderSection,
    selectedFeature,
    arco,
    geoData,
    availableDates,
    selectedDate,
    setSelectedDate,
    selectedData,
    radarSelected,
    networkViewDate,
    setNetworkViewDate,
    networkViewTime,
    setNetworkViewTime,
    availableNetworkDates,
    availableNetworkTimes,
    networkViewCategory,
    onHighlightRoads,
    onZoomToRoad,
    onCloseSider
}) {
    const [siderWidth, setSiderWidth] = useState(500);
    const [isResizing, setIsResizing] = useState(false);
    const [expandedVideo, setExpandedVideo] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [playSpeed, setPlaySpeed] = useState(800); // Default 1x speed
    const playIntervalRef = useRef(null);
    const siderRef = useRef(null);

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isResizing) return;

            const newWidth = e.clientX;
            if (newWidth >= 300 && newWidth <= 800) {
                setSiderWidth(newWidth);
            }
        };

        const handleMouseUp = () => {
            setIsResizing(false);
        };

        if (isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isResizing]);

    const handleMouseDown = useCallback((e) => {
        e.preventDefault();
        setIsResizing(true);
    }, []);

    const handleMouseEnter = useCallback((e) => {
        e.target.style.backgroundColor = '#bbb';
    }, []);

    const handleMouseLeave = useCallback((e) => {
        e.target.style.backgroundColor = '#e0e0e0';
    }, []);

    // Play/Pause functionality
    const handlePlayPause = useCallback(() => {
        if (isPlaying) {
            // Stop playing
            setIsPlaying(false);
            if (playIntervalRef.current) {
                clearInterval(playIntervalRef.current);
                playIntervalRef.current = null;
            }
        } else {
            // Start playing
            setIsPlaying(true);

            // Start from the beginning if we're at the end
            if (availableNetworkTimes && availableNetworkTimes.length > 0) {
                const currentIndex = availableNetworkTimes.indexOf(networkViewTime);
                if (currentIndex === availableNetworkTimes.length - 1) {
                    setNetworkViewTime(availableNetworkTimes[0]);
                }

                // Set up interval to advance time
                playIntervalRef.current = setInterval(() => {
                    setNetworkViewTime(prevTime => {
                        const currentIdx = availableNetworkTimes.indexOf(prevTime);
                        if (currentIdx >= availableNetworkTimes.length - 1) {
                            // Reached the end, stop playing
                            setIsPlaying(false);
                            if (playIntervalRef.current) {
                                clearInterval(playIntervalRef.current);
                                playIntervalRef.current = null;
                            }
                            return availableNetworkTimes[availableNetworkTimes.length - 1];
                        }
                        return availableNetworkTimes[currentIdx + 1];
                    });
                }, playSpeed); // Use configurable speed
            }
        }
    }, [isPlaying, availableNetworkTimes, networkViewTime, setNetworkViewTime, playSpeed]);

    // Clean up interval on unmount or when times change
    useEffect(() => {
        return () => {
            if (playIntervalRef.current) {
                clearInterval(playIntervalRef.current);
            }
        };
    }, []);

    // Stop playing when date or section changes
    useEffect(() => {
        setIsPlaying(false);
        if (playIntervalRef.current) {
            clearInterval(playIntervalRef.current);
            playIntervalRef.current = null;
        }
    }, [networkViewDate, siderSection]);


    const renderSiderContent = () => (
        <div>
            {/* Proprieta - shown for "proprieta" or "velocita" sections when roads are selected */}
            {(siderSection === "proprieta" || siderSection === "velocita") && arco && arco.length > 0 && (
                <Proprieta
                    arco={arco}
                    selectedFeature={selectedFeature}
                    availableDates={availableDates}
                    selectedDate={selectedDate}
                    setSelectedDate={setSelectedDate}
                    selectedData={selectedData}
                    radarSelected={radarSelected}
                />
            )}

            {siderSection === "flow" && arco && arco.length > 0 && (
                <Flow
                    arco={arco}
                    selectedDate={selectedDate}
                    setSelectedDate={setSelectedDate}
                    selectedData={selectedData}
                    radarSelected={radarSelected}
                />
            )}

            {siderSection === "traveltime" && arco && arco.length > 0 && (
                <TravelTime
                    arco={arco}
                    selectedDate={selectedDate}
                    setSelectedDate={setSelectedDate}
                    selectedData={selectedData}
                    radarSelected={radarSelected}
                />
            )}

            {/* Network view cards when no road is selected */}
            {siderSection === "velocita" && (!arco || arco.length === 0) && (
                <div className="proprieta-wrapper">
                    <div className="proprieta-title">
                        <img src={VelocitaIcon} alt="Velocità" className="proprieta-icon-img" />
                        <h3>Velocità</h3>
                    </div>
                    <div className="proprieta-content anomaly-content">
                        <p className="proprieta-description anomaly-description">
                            Seleziona la data per la quale vuoi visualizzare i valori.
                        </p>
                        <Select
                            placeholder="Seleziona data"
                            className="anomaly-date-picker"
                            value={Array.isArray(networkViewDate) ? networkViewDate[0] : networkViewDate}
                            onChange={(date) => {
                                setNetworkViewDate(date);
                            }}
                            getPopupContainer={(trigger) => trigger.parentElement}
                            popupStyle={{ zIndex: 9999 }}
                            style={{ borderRadius: "50px !important" }}
                        >
                            {availableNetworkDates.map(date => (
                                <Select.Option key={date} value={date}>
                                    {date && date.match(/^\d{4}-\d{2}-\d{2}$/) ? dayjs(date).format('DD/MM/YYYY') : date}
                                </Select.Option>
                            ))}
                        </Select>
                    </div>
                    <div className="network-view-time-display">{networkViewTime || (availableNetworkTimes && availableNetworkTimes.length > 0 ? availableNetworkTimes[0] : '00:00')}</div>

                    <div className="network-view-time-section">
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px', width: '100%' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', width: "100%" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "10px", width: "100%" }}>
                                    <span className="network-view-time-label">{availableNetworkTimes && availableNetworkTimes.length > 0 ? availableNetworkTimes[0] : '00:00'}</span>
                                    <input
                                        type="range"
                                        min="0"
                                        max={availableNetworkTimes && availableNetworkTimes.length > 0 ? availableNetworkTimes.length - 1 : 0}
                                        value={availableNetworkTimes && networkViewTime ? availableNetworkTimes.indexOf(networkViewTime) : 0}
                                        onChange={(e) => {
                                            const index = parseInt(e.target.value);
                                            if (availableNetworkTimes && availableNetworkTimes[index]) {
                                                setNetworkViewTime(availableNetworkTimes[index]);
                                            }
                                        }}
                                        className="network-view-time-slider"
                                    />
                                    <span className="network-view-time-label">{availableNetworkTimes && availableNetworkTimes.length > 0 ? availableNetworkTimes[availableNetworkTimes.length - 1] : '23:59'}</span>

                                </div>
                                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                    <Select
                                        value={playSpeed}
                                        onChange={setPlaySpeed}
                                        style={{ width: 100, height: "32px" }}
                                        size="small"
                                        disabled={isPlaying}
                                        getPopupContainer={(trigger) => trigger.parentElement}
                                        popupStyle={{ zIndex: 10000000 }}
                                        optionLabelProp="label"
                                        options={[
                                            {
                                                label: "2.5x",
                                                value: 50
                                            },
                                            {
                                                label: "2x",
                                                value: 300
                                            },
                                            {
                                                label: "1.5x",
                                                value: 550
                                            },
                                            {
                                                label: "1x",
                                                value: 800
                                            },
                                            {
                                                label: "0.5x",
                                                value: 1800
                                            }
                                        ]}
                                    />
                                    <Button
                                        type="primary"
                                        shape="circle"
                                        icon={isPlaying ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                                        onClick={handlePlayPause}
                                        size="large"
                                        style={{
                                            backgroundColor: isPlaying ? '#ff4d4f' : 'rgba(161, 228, 48, 1)',
                                            borderColor: isPlaying ? '#ff4d4f' : 'rgba(161, 228, 48, 1)'
                                        }}
                                        title={isPlaying ? 'Pausa' : 'Riproduci'}
                                    />
                                </div>
                            </div>
                        </div>

                    </div>

                    <div className="network-view-legend">
                        <div className="network-view-legend-header">
                            <p className="network-view-legend-title">Legenda Velocità (km/h)</p>
                        </div>
                        <div className="legend-gradient-container">
                            <div className="legend-gradient-bar gradient-velocita"></div>
                            <div className="legend-labels">
                                <span className="legend-label">Bassa</span>
                                <span className="legend-label">Alta</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {siderSection === "flow" && (!arco || arco.length === 0) && (
                <div className="proprieta-wrapper">
                    <div className="proprieta-title">
                        <img src={FlowIcon} alt="Flusso" className="proprieta-icon-img" />
                        <h3>Flusso</h3>
                    </div>
                    <div className="proprieta-content anomaly-content">
                        <p className="proprieta-description anomaly-description">
                            Seleziona la data per la quale vuoi visualizzare i valori.
                        </p>
                        <Select
                            placeholder="Seleziona data"
                            className="anomaly-date-picker"
                            value={Array.isArray(networkViewDate) ? networkViewDate[0] : networkViewDate}
                            onChange={(date) => setNetworkViewDate(date)}
                            getPopupContainer={(trigger) => trigger.parentElement}
                            popupStyle={{ zIndex: 9999 }}
                            style={{ borderRadius: "50px !important" }}
                        >
                            {availableNetworkDates.map(date => (
                                <Select.Option key={date} value={date}>
                                    {date && date.match(/^\d{4}-\d{2}-\d{2}$/) ? dayjs(date).format('DD/MM/YYYY') : date}
                                </Select.Option>
                            ))}
                        </Select>
                    </div>
                    <div className="network-view-time-display">{networkViewTime || (availableNetworkTimes && availableNetworkTimes.length > 0 ? availableNetworkTimes[0] : '00:00')}</div>

                    <div className="network-view-time-section">
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px', width: '100%' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', width: "100%" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "10px", width: "100%" }}>
                                    <span className="network-view-time-label">{availableNetworkTimes && availableNetworkTimes.length > 0 ? availableNetworkTimes[0] : '00:00'}</span>
                                    <input
                                        type="range"
                                        min="0"
                                        max={availableNetworkTimes && availableNetworkTimes.length > 0 ? availableNetworkTimes.length - 1 : 0}
                                        value={availableNetworkTimes && networkViewTime ? availableNetworkTimes.indexOf(networkViewTime) : 0}
                                        onChange={(e) => {
                                            const index = parseInt(e.target.value);
                                            if (availableNetworkTimes && availableNetworkTimes[index]) {
                                                setNetworkViewTime(availableNetworkTimes[index]);
                                            }
                                        }}
                                        className="network-view-time-slider"
                                    />
                                    <span className="network-view-time-label">{availableNetworkTimes && availableNetworkTimes.length > 0 ? availableNetworkTimes[availableNetworkTimes.length - 1] : '23:59'}</span>

                                </div>
                                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                    <Select
                                        value={playSpeed}
                                        onChange={setPlaySpeed}
                                        style={{ width: 100, height: "32px" }}
                                        size="small"
                                        disabled={isPlaying}
                                        getPopupContainer={(trigger) => trigger.parentElement}
                                        popupStyle={{ zIndex: 10000000 }}
                                        optionLabelProp="label"
                                        options={[
                                            {
                                                label: "2.5x",
                                                value: 50
                                            },
                                            {
                                                label: "2x",
                                                value: 300
                                            },
                                            {
                                                label: "1.5x",
                                                value: 550
                                            },
                                            {
                                                label: "1x",
                                                value: 800
                                            },
                                            {
                                                label: "0.5x",
                                                value: 1800
                                            }
                                        ]}
                                    />
                                    <Button
                                        type="primary"
                                        shape="circle"
                                        icon={isPlaying ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                                        onClick={handlePlayPause}
                                        size="large"
                                        style={{
                                            backgroundColor: isPlaying ? '#ff4d4f' : 'rgba(161, 228, 48, 1)',
                                            borderColor: isPlaying ? '#ff4d4f' : 'rgba(161, 228, 48, 1)'
                                        }}
                                        title={isPlaying ? 'Pausa' : 'Riproduci'}
                                    />
                                </div>
                            </div>
                        </div>

                    </div>

                    <div className="network-view-legend">
                        <div className="network-view-legend-header">
                            <p className="network-view-legend-title">Legenda Flusso (VEH)</p>
                        </div>
                        <div className="legend-gradient-container">
                            <div className="legend-gradient-bar gradient-flow"></div>
                            <div className="legend-labels">
                                <span className="legend-label">Basso</span>
                                <span className="legend-label">Alto</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {siderSection === "traveltime" && (!arco || arco.length === 0) && (
                <div className="proprieta-wrapper">
                    <div className="proprieta-title">
                        <img src={TravelTimeIcon} alt="Tempo di Percorrenza" className="proprieta-icon-img" />
                        <h3>Tempo di Percorrenza</h3>
                    </div>
                    <div className="proprieta-content anomaly-content">
                        <p className="proprieta-description anomaly-description">
                            Seleziona la data per la quale vuoi visualizzare i valori.
                        </p>
                        <Select
                            placeholder="Seleziona data"
                            className="anomaly-date-picker"
                            value={Array.isArray(networkViewDate) ? networkViewDate[0] : networkViewDate}
                            onChange={(date) => {
                                setNetworkViewDate(date);
                            }}
                            getPopupContainer={(trigger) => trigger.parentElement}
                            popupStyle={{ zIndex: 9999 }}
                            style={{ borderRadius: "50px !important" }}
                        >
                            {availableNetworkDates.map(date => (
                                <Select.Option key={date} value={date}>
                                    {date && date.match(/^\d{4}-\d{2}-\d{2}$/) ? dayjs(date).format('DD/MM/YYYY') : date}
                                </Select.Option>
                            ))}
                        </Select>
                    </div>
                    <div className="network-view-time-display">{networkViewTime || (availableNetworkTimes && availableNetworkTimes.length > 0 ? availableNetworkTimes[0] : '00:00')}</div>

                    <div className="network-view-time-section">
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px', width: '100%' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', width: "100%" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "10px", width: "100%" }}>
                                    <span className="network-view-time-label">{availableNetworkTimes && availableNetworkTimes.length > 0 ? availableNetworkTimes[0] : '00:00'}</span>
                                    <input
                                        type="range"
                                        min="0"
                                        max={availableNetworkTimes && availableNetworkTimes.length > 0 ? availableNetworkTimes.length - 1 : 0}
                                        value={availableNetworkTimes && networkViewTime ? availableNetworkTimes.indexOf(networkViewTime) : 0}
                                        onChange={(e) => {
                                            const index = parseInt(e.target.value);
                                            if (availableNetworkTimes && availableNetworkTimes[index]) {
                                                setNetworkViewTime(availableNetworkTimes[index]);
                                            }
                                        }}
                                        className="network-view-time-slider"
                                    />
                                    <span className="network-view-time-label">{availableNetworkTimes && availableNetworkTimes.length > 0 ? availableNetworkTimes[availableNetworkTimes.length - 1] : '23:59'}</span>

                                </div>
                                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                    <Select
                                        value={playSpeed}
                                        onChange={setPlaySpeed}
                                        style={{ width: 100, height: "32px" }}
                                        size="small"
                                        disabled={isPlaying}
                                        getPopupContainer={(trigger) => trigger.parentElement}
                                        popupStyle={{ zIndex: 10000000 }}
                                        optionLabelProp="label"
                                        options={[
                                            {
                                                label: "2.5x",
                                                value: 50
                                            },
                                            {
                                                label: "2x",
                                                value: 300
                                            },
                                            {
                                                label: "1.5x",
                                                value: 550
                                            },
                                            {
                                                label: "1x",
                                                value: 800
                                            },
                                            {
                                                label: "0.5x",
                                                value: 1800
                                            }
                                        ]}
                                    />
                                    <Button
                                        type="primary"
                                        shape="circle"
                                        icon={isPlaying ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                                        onClick={handlePlayPause}
                                        size="large"
                                        style={{
                                            backgroundColor: isPlaying ? '#ff4d4f' : 'rgba(161, 228, 48, 1)',
                                            borderColor: isPlaying ? '#ff4d4f' : 'rgba(161, 228, 48, 1)'
                                        }}
                                        title={isPlaying ? 'Pausa' : 'Riproduci'}
                                    />
                                </div>
                            </div>
                        </div>

                    </div>

                    <div className="network-view-legend">
                        <div className="network-view-legend-header">
                            <p className="network-view-legend-title">
                                Legenda Colori
                            </p>
                        </div>
                        <div className="legend-gradient-container">
                            <div className={`legend-gradient-bar ${networkViewCategory === 'flow' ? 'gradient-flow' : 'gradient-traveltime'}`}></div>
                            <div className="legend-labels">
                                <span className="legend-label">{networkViewCategory === 'flow' ? 'Basso' : 'Tempo breve'}</span>
                                <span className="legend-label">{networkViewCategory === 'flow' ? 'Alto' : 'Tempo lungo'}</span>
                            </div>

                        </div>
                    </div>
                </div>
            )}

            {siderSection === "ambientali" && (
                <Ambientali
                    arco={arco}
                    selectedDate={selectedDate}
                    radarSelected={radarSelected}
                />
            )}

            {siderSection === "nuvola" && (
                <div style={{ height: "500px", width: "100%" }}>
                    <PointCloudDeckGL />
                </div>
            )}

            {siderSection === "audio" && (
                <AudioChart />
            )}

            {siderSection === "accelerometrici" && (
                <div style={{ padding: 24 }}>
                    <Imu
                        arco={arco}
                        selectedDate={selectedDate}
                        radarSelected={radarSelected}
                    />
                </div>
            )}

            {siderSection === "video" && (
                <div className="sider-video-container">
                    {/* Header */}
                    <div className="sider-video-header">
                        <BiVideoRecording />
                        <h3 className="sider-video-header-title">
                            Rilevamento di anomalie video
                        </h3>
                    </div>

                    {/* Video Cards Container */}
                    <div className="sider-video-cards">
                        {/* Persone in strada */}
                        <div className="sider-video-card">
                            <h4 className="sider-video-card-header">
                                Persone in strada
                            </h4>
                            <div className="sider-video-wrapper" style={{ position: 'relative' }}>
                                <button
                                    onClick={() => setExpandedVideo({ title: 'Persone in strada', src: '/videos/persone_in_strada.webm' })}
                                    style={{
                                        position: 'absolute',
                                        top: '10px',
                                        right: '10px',
                                        width: '36px',
                                        height: '36px',
                                        backgroundColor: 'rgba(24, 144, 255, 0.9)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '16px',
                                        zIndex: 100,
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                        transition: 'all 0.3s'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = 'rgba(64, 169, 255, 1)';
                                        e.currentTarget.style.transform = 'scale(1.1)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'rgba(24, 144, 255, 0.9)';
                                        e.currentTarget.style.transform = 'scale(1)';
                                    }}
                                    title="Espandi video"
                                >
                                    <ExpandOutlined />
                                </button>
                                <video
                                    src="/videos/persone_in_strada.webm"
                                    autoPlay
                                    muted
                                    loop
                                    controls
                                    playsInline
                                    className="sider-video-element"
                                />
                            </div>
                        </div>

                        {/* Persone in strada 2 */}
                        <div className="sider-video-card">
                            <h4 className="sider-video-card-header">
                                Persone in strada
                            </h4>
                            <div className="sider-video-wrapper" style={{ position: 'relative' }}>
                                <button
                                    onClick={() => setExpandedVideo({ title: 'Persone in strada', src: '/videos/persone_in_strada.webm' })}
                                    style={{
                                        position: 'absolute',
                                        top: '10px',
                                        right: '10px',
                                        width: '36px',
                                        height: '36px',
                                        backgroundColor: 'rgba(24, 144, 255, 0.9)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '16px',
                                        zIndex: 100,
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                        transition: 'all 0.3s'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = 'rgba(64, 169, 255, 1)';
                                        e.currentTarget.style.transform = 'scale(1.1)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'rgba(24, 144, 255, 0.9)';
                                        e.currentTarget.style.transform = 'scale(1)';
                                    }}
                                    title="Espandi video"
                                >
                                    <ExpandOutlined />
                                </button>
                                <video
                                    src="/videos/persone_in_strada.webm"
                                    autoPlay
                                    muted
                                    loop
                                    controls
                                    playsInline
                                    className="sider-video-element"
                                />
                            </div>
                        </div>

                        {/* Blocco stradale */}
                        <div className="sider-video-card">
                            <h4 className="sider-video-card-header">
                                Blocco stradale
                            </h4>
                            <div className="sider-video-wrapper" style={{ position: 'relative' }}>
                                <button
                                    onClick={() => setExpandedVideo({ title: 'Blocco stradale', src: '/videos/blocking.webm' })}
                                    style={{
                                        position: 'absolute',
                                        top: '10px',
                                        right: '10px',
                                        width: '36px',
                                        height: '36px',
                                        backgroundColor: 'rgba(24, 144, 255, 0.9)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '16px',
                                        zIndex: 100,
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                        transition: 'all 0.3s'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = 'rgba(64, 169, 255, 1)';
                                        e.currentTarget.style.transform = 'scale(1.1)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'rgba(24, 144, 255, 0.9)';
                                        e.currentTarget.style.transform = 'scale(1)';
                                    }}
                                    title="Espandi video"
                                >
                                    <ExpandOutlined />
                                </button>
                                <video
                                    src="/videos/blocking.webm"
                                    autoPlay
                                    muted
                                    loop
                                    controls
                                    playsInline
                                    className="sider-video-element"
                                />
                            </div>
                        </div>

                        {/* Blocco stradale 2 */}
                        <div className="sider-video-card">
                            <h4 className="sider-video-card-header">
                                Blocco stradale 2
                            </h4>
                            <div className="sider-video-wrapper" style={{ position: 'relative' }}>
                                <button
                                    onClick={() => setExpandedVideo({ title: 'Blocco stradale 2', src: '/videos/blocking2.webm' })}
                                    style={{
                                        position: 'absolute',
                                        top: '10px',
                                        right: '10px',
                                        width: '36px',
                                        height: '36px',
                                        backgroundColor: 'rgba(24, 144, 255, 0.9)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '16px',
                                        zIndex: 100,
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                        transition: 'all 0.3s'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = 'rgba(64, 169, 255, 1)';
                                        e.currentTarget.style.transform = 'scale(1.1)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'rgba(24, 144, 255, 0.9)';
                                        e.currentTarget.style.transform = 'scale(1)';
                                    }}
                                    title="Espandi video"
                                >
                                    <ExpandOutlined />
                                </button>
                                <video
                                    src="/videos/blocking2.webm"
                                    autoPlay
                                    muted
                                    loop
                                    controls
                                    playsInline
                                    className="sider-video-element"
                                />
                            </div>
                        </div>

                        {/* Contromano */}
                        <div className="sider-video-card">
                            <h4 className="sider-video-card-header">
                                Contromano
                            </h4>
                            <div className="sider-video-wrapper" style={{ position: 'relative' }}>
                                <button
                                    onClick={() => setExpandedVideo({ title: 'Contromano', src: '/videos/contromano.webm' })}
                                    style={{
                                        position: 'absolute',
                                        top: '10px',
                                        right: '10px',
                                        width: '36px',
                                        height: '36px',
                                        backgroundColor: 'rgba(24, 144, 255, 0.9)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '16px',
                                        zIndex: 100,
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                        transition: 'all 0.3s'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = 'rgba(64, 169, 255, 1)';
                                        e.currentTarget.style.transform = 'scale(1.1)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'rgba(24, 144, 255, 0.9)';
                                        e.currentTarget.style.transform = 'scale(1)';
                                    }}
                                    title="Espandi video"
                                >
                                    <ExpandOutlined />
                                </button>
                                <video
                                    src="/videos/contromano.webm"
                                    autoPlay
                                    muted
                                    loop
                                    controls
                                    playsInline
                                    className="sider-video-element"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {siderSection === "confronto" && (
                <ConfrontoIMUGraph arco={arco} radarSelected={radarSelected} />
            )}

            {siderSection === "info" && (
                <Info
                    arco={arco}
                    radarSelected={radarSelected}
                    geoData={geoData}
                />
            )}

            {siderSection === "ai" && (
                <Interpreter
                    onHighlightRoads={onHighlightRoads}
                    onZoomToRoad={onZoomToRoad}
                    onClose={onCloseSider}
                    geoData={geoData}
                />
            )}
        </div>
    );

    if (isMobile) {
        return (
            <>
                <div className="sider-mobile-container">
                    {renderSiderContent()}
                </div>

                {/* Expanded Video Modal */}
                <Modal
                    open={expandedVideo !== null}
                    onCancel={() => setExpandedVideo(null)}
                    footer={null}
                    width="95%"
                    centered
                    title={expandedVideo?.title || 'Video - Vista Espansa'}
                    destroyOnClose
                    zIndex={9999}
                    style={{ top: 20 }}
                >
                    <div style={{ padding: '20px 0', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        {expandedVideo && (
                            <video
                                src={expandedVideo.src}
                                autoPlay
                                muted
                                loop
                                controls
                                playsInline
                                style={{
                                    width: '100%',
                                    maxWidth: '1200px',
                                    height: 'auto',
                                    borderRadius: '8px'
                                }}
                            />
                        )}
                    </div>
                </Modal>
            </>
        );
    }

    return (
        <>
            <div className={`sider-resizable-wrapper ${isResizing ? 'no-select' : ''}`}>
                <Sider
                    collapsible
                    collapsed={collapsed}
                    onCollapse={setCollapsed}
                    width={siderWidth}
                    collapsedWidth="0"
                    ref={siderRef}
                    style={{
                        overflowY: "auto",
                        overflowX: "hidden",
                        height: 'calc(100vh - 64px - 69px)',
                        transition: collapsed ? 'width 0.2s' : 'none',
                        border: "none !important",
                    }}
                >
                    {renderSiderContent()}
                </Sider>

                {!collapsed && (
                    <div
                        className="sider-resize-handle"
                        onMouseDown={handleMouseDown}
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                    >
                        <div className="sider-resize-indicator">
                            ⋮⋮⋮
                        </div>
                    </div>
                )}
            </div>

            {/* Expanded Video Modal */}
            <Modal
                open={expandedVideo !== null}
                onCancel={() => setExpandedVideo(null)}
                footer={null}
                width="95%"
                centered
                title={expandedVideo?.title || 'Video - Vista Espansa'}
                destroyOnClose
                zIndex={9999}
                style={{ top: 20 }}
            >
                <div style={{ padding: '20px 0', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    {expandedVideo && (
                        <video
                            src={expandedVideo.src}
                            autoPlay
                            muted
                            loop
                            controls
                            playsInline
                            style={{
                                width: '100%',
                                maxWidth: '1200px',
                                height: 'auto',
                                borderRadius: '8px'
                            }}
                        />
                    )}
                </div>
            </Modal>
        </>
    );
}

export default memo(SiderComponent);
